import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import { PerksIcon } from '../../../../components/icons';
import { PerksDrawer, PerksSafoniaDrawer } from '../../../../components/perks-drawer';
import { useShippingProtectionConfigs } from '../../../../hooks/useShippingProtectionConfigs';
import { Button, Text } from '../../../../ui-components';
import {
    getMissingValueForNextPerksValue,
    getNextPerksValue,
    getPerksValue,
} from '../../../../utils/perks-utils';

interface CartPerksProps {
    shippingProtectionBonus?: boolean;
}

export const CartPerks: React.FC<CartPerksProps> = ({ shippingProtectionBonus = false }) => {
    const { data } = useBasket();
    const { isOpen, onClose, onOpen } = useDisclosure();

    const { isUIVisible } = useShippingProtectionConfigs();

    const component = useCallback(
        (chunks: ReactNode[]) => (
            <Button
                id="cart-perks-button"
                variant="text-button"
                color="slt-primary-green.1000"
                textStyle="bold"
                textSize="base"
                onClick={onOpen}
            >
                {chunks}
            </Button>
        ),
        [],
    );

    if (!data || window.rewardsMessaging !== 'PDP&CART') {
        return null;
    }

    const { c_productSubTotalIncludingAdjustments: productSubTotal, c_shippingProtectionFee } =
        data;

    // When shippingProtectionBonus is true, calculate perks based on product value excluding shipping protection
    const adjustedSubTotal =
        shippingProtectionBonus && c_shippingProtectionFee
            ? (productSubTotal ?? 0) - Number(c_shippingProtectionFee)
            : (productSubTotal ?? 0);

    const perks = `$${getPerksValue(adjustedSubTotal, shippingProtectionBonus)}`;
    const nextPerks = `$${getNextPerksValue(adjustedSubTotal)}`;
    const missingValue = getMissingValueForNextPerksValue(adjustedSubTotal);

    return (
        <Flex
            data-testid="cart-perks"
            alignItems="center"
            marginLeft={4}
            borderColor="slt-pepper.300"
            borderRadius={4}
            borderWidth={1}
            padding={3}
            paddingLeft={8}
            minHeight="70px"
            position="relative"
        >
            <Box
                background="slt-beige.400"
                borderRadius={24}
                position="absolute"
                left={-6}
                padding={2}
            >
                <PerksIcon data-testid="cart-perks-icon" color="slt-primary-green.1000" />
            </Box>

            <Flex direction="column">
                <Text id="cart-perks-message-earned">
                    <FormattedMessage
                        id="buy.box.earned.perks"
                        defaultMessage="You've earned <component>{perks} in Perks</component>!"
                        values={{
                            component,
                            perks,
                        }}
                    />
                </Text>
                {missingValue != null && (
                    <Text id="cart-perks-message-spend-more" color="slt-pepper.800" size="small">
                        <FormattedMessage
                            id="buy.box.spend.more.to.earn.perks"
                            defaultMessage="Spend {missingValue} more to earn {nextPerks} in Perks."
                            values={{
                                missingValue: `$${
                                    Math.round((missingValue + Number.EPSILON) * 100) / 100
                                }`,
                                nextPerks,
                            }}
                        />
                    </Text>
                )}
            </Flex>

            {isUIVisible ? (
                <PerksSafoniaDrawer
                    id="cart-perks-drawer-safonia"
                    isOpen={isOpen}
                    onClose={onClose}
                />
            ) : (
                <PerksDrawer id="cart-perks-drawer" isOpen={isOpen} onClose={onClose} />
            )}
        </Flex>
    );
};
